import type { CollectionStatusEnum } from "@kanpla/api-contract";
import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren
} from "react";

import { Avatar } from "~/components/avatar";
import {
  collectionStatusCardBg,
  collectionStatusIndicator
} from "~/components/collect/collection-status-styles";
import { cn } from "~/lib/utils";

type UserCardContextValue = {
  status: CollectionStatusEnum;
};

const UserCardContext = createContext<UserCardContextValue | null>(null);

const useUserCardContext = () => {
  const context = useContext(UserCardContext);

  if (!context) {
    throw new Error(
      "UserCard compound components must be used within UserCard"
    );
  }

  return context;
};

export type UserCardProps = PropsWithChildren<{
  status: CollectionStatusEnum;
  onClick?: () => void;
  className?: string;
}>;

/**
 * Status-tinted user row for the collect operator grid. Compose with
 * {@link UserCardAvatar}, {@link UserCardContent}, and
 * {@link UserCardStatusIndicator}.
 */
const UserCardRoot = ({
  status,
  onClick,
  className,
  children
}: UserCardProps) => (
  <UserCardContext.Provider value={{ status }}>
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 p-3 text-left",
        collectionStatusCardBg({ status }),
        className
      )}
    >
      {children}
    </button>
  </UserCardContext.Provider>
);

export type UserCardAvatarProps = {
  name: string;
  avatarUrl?: string;
  className?: string;
};

const UserCardAvatar = ({
  name,
  avatarUrl,
  className
}: UserCardAvatarProps) => {
  const [failedAvatarUrl, setFailedAvatarUrl] = useState<string>();
  const showInitials = !avatarUrl || failedAvatarUrl === avatarUrl;

  if (showInitials) {
    return (
      <Avatar name={name} size="sm" className={cn("flex-none", className)} />
    );
  }

  return (
    <img
      src={avatarUrl}
      alt=""
      className={cn(
        "size-10 flex-none rounded-full bg-interface-neutral-2 object-cover",
        className
      )}
      onError={() => {
        setFailedAvatarUrl(avatarUrl);
      }}
    />
  );
};

const UserCardContent = ({
  children,
  className
}: PropsWithChildren<{ className?: string }>) => (
  <div className={cn("min-w-0 flex-1", className)}>{children}</div>
);

const UserCardName = ({
  children,
  className
}: PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      "truncate text-base font-semibold text-interface-neutral-6",
      className
    )}
  >
    {children}
  </div>
);

const UserCardGroup = ({
  children,
  className
}: PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      "mt-0.5 truncate text-sm text-interface-neutral-5",
      className
    )}
  >
    {children}
  </div>
);

const UserCardStatusIndicator = ({ className }: { className?: string }) => {
  const { status } = useUserCardContext();

  return (
    <span
      className={cn(
        "size-2.5 flex-none rounded-full",
        collectionStatusIndicator({ status }),
        className
      )}
    />
  );
};

export const UserCard = Object.assign(UserCardRoot, {
  Avatar: UserCardAvatar,
  Content: UserCardContent,
  Name: UserCardName,
  Group: UserCardGroup,
  StatusIndicator: UserCardStatusIndicator
});
